import React, { useCallback, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { ElectronicSignature } from '~/components/SignatureVerification/ElectronicSignature/ElectronicSignature.tsx';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { RejectCommentsModal } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ReviewDocuments/RejectCommentsModal.tsx';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ApplicationHeader from '../../shared/components/ApplicationHeader.tsx';
import ApplicationPageFrame from '../../shared/components/ApplicationPageFrame.tsx';
import GuaranteesManagerApplicationSidePanelLayout from '../shared/SidePanel/GuaranteesManagerApplicationSidePanelLayout.tsx';
import {
  UDBTF_APPROVAL_LETTER_MISSING_PDF_TEXT,
  UDBTF_APPROVAL_LETTER_REVOKE_MODAL_TEXT,
  UDBTF_APPROVAL_LETTER_TASKS_PATH,
} from './constants.ts';
import InternalApprovalLetterDocumentSection from './InternalApprovalLetterDocumentSection.tsx';
import { useInternalApprovalConclusionAttachment } from './useInternalApprovalConclusionAttachment.ts';
import { useInternalApprovalPage } from './useInternalApprovalPage.ts';
import { useInternalApprovalPdfPreview } from './useInternalApprovalPdfPreview.ts';
import { useInternalApprovalSigning } from './useInternalApprovalSigning.ts';
import { useInternalApprovalStatusUpdate } from './useInternalApprovalStatusUpdate.ts';
import {
  getApprovalLetterPreviewFileId,
  getInternalApprovalSigningPreviewTarget,
  getInternalApprovalLetterFile,
} from './utils.ts';

const InternalApprovalLetterPage: React.FC = () => {
  const {
    application,
    applicationFiles,
    approvalComment,
    approvalConfig,
    approvalType,
    contragentId,
    dispatch,
    effectiveApplicationId,
    generateConclusionAttachmentStatus,
    handleCloseAlertModal,
    handleCloseSuccessModal,
    handleCloseUdbtfAccessModal,
    headerProps,
    hasApplicationFilesLoaded,
    isApplicationFilesLoading,
    isApplicationLoading,
    isUdbtfAccessDenied,
    isUdbtfAccessLoading,
    modalAlert,
    modalSuccess,
    navigateToTasks,
    redirectPath,
    refreshApplicationFiles,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
  } = useInternalApprovalPage();
  const approvalLetterFile = useMemo(
    () => getInternalApprovalLetterFile(applicationFiles, approvalConfig.fileType),
    [applicationFiles, approvalConfig.fileType]
  );
  const [revokeRedirectPath, setRevokeRedirectPath] = useState('');
  const { handleOpenPdf, isDocumentLoading, pdfUrl } = useInternalApprovalPdfPreview({
    approvalLetterFile,
    hasApplicationFilesLoaded,
    setModalAlert,
    showGuaranteesRequestError,
  });
  const {
    additionalDescription,
    conclusionAttachmentFile,
    conclusionPdfUrl,
    handleAdditionalDescriptionConfirm,
    handleOpenConclusionPdf,
    isConclusionAttachmentRequired,
    isConclusionDocumentLoading,
    isConclusionGenerating,
    setAdditionalDescription,
  } = useInternalApprovalConclusionAttachment({
    application,
    applicationFiles,
    approvalComment,
    approvalConfig,
    approvalType,
    contragentId,
    dispatch,
    effectiveApplicationId,
    generateConclusionAttachmentStatus,
    hasApplicationFilesLoaded,
    isApplicationFilesLoading,
    refreshApplicationFiles,
    setModalAlert,
    showGuaranteesRequestError,
  });
  const [revokeComment, setRevokeComment] = useState('');
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const { approveApplication, denyApplication, isUdbtfApprovalStatusUpdating } =
    useInternalApprovalStatusUpdate({
      application,
      approvalType,
      dispatch,
      effectiveApplicationId,
      navigateToTasks,
      setModalAlert,
      setModalSuccess,
      showGuaranteesRequestError,
    });
  const {
    deepLink,
    handleCloseSignatureModal,
    handleDiiaSignature,
    handleIITSignature,
    handleSignAndConfirm,
    handleTimeUp,
    isIITLoading,
    isSignatureModalOpen,
    onIITSign,
    signingDocuments,
    timerRef,
  } = useInternalApprovalSigning({
    approvalLetterFile,
    approvalLetterFileType: approvalConfig.fileType,
    conclusionAttachmentFile,
    conclusionAttachmentFileType: approvalConfig.conclusionDocument?.fileType,
    conclusionPreviewTitle: approvalConfig.conclusionDocument?.previewTitle,
    contragentId,
    dispatch,
    effectiveApplicationId,
    isConclusionAttachmentRequired: !!approvalConfig.conclusionDocument,
    onApprove: approveApplication,
    previewTitle: approvalConfig.texts.previewTitle,
    refreshApplicationFiles,
    setModalAlert,
    showGuaranteesRequestError,
  });
  const handleRevokeConfirm = async () => {
    const isSuccess = await denyApplication(revokeComment);

    if (isSuccess) {
      setIsRevokeModalOpen(false);
      setRevokeComment('');
      setRevokeRedirectPath(UDBTF_APPROVAL_LETTER_TASKS_PATH);
    }
  };
  const handleSignaturePreview = useCallback(
    (documentId?: number | string) => {
      const previewTarget = getInternalApprovalSigningPreviewTarget({
        documents: signingDocuments,
        documentId,
        approvalLetterFileId: getApprovalLetterPreviewFileId(approvalLetterFile),
        conclusionAttachmentFileId: getApprovalLetterPreviewFileId(conclusionAttachmentFile),
      });
      if (previewTarget === 'approval') {
        handleOpenPdf();
        return;
      }
      if (previewTarget === 'conclusion') {
        handleOpenConclusionPdf();
        return;
      }
      setModalAlert({ text: UDBTF_APPROVAL_LETTER_MISSING_PDF_TEXT });
    },
    [
      approvalLetterFile,
      conclusionAttachmentFile,
      handleOpenConclusionPdf,
      handleOpenPdf,
      setModalAlert,
      signingDocuments,
    ]
  );

  if (redirectPath || revokeRedirectPath) {
    return <Navigate to={redirectPath || revokeRedirectPath} replace />;
  }

  if (isUdbtfAccessLoading) {
    return <FullPageLoader />;
  }

  return (
    <ApplicationPageFrame
      modalAlert={modalAlert}
      modalSuccess={modalSuccess}
      onModalClose={isUdbtfAccessDenied ? handleCloseUdbtfAccessModal : handleCloseAlertModal}
      onSuccessModalClose={handleCloseSuccessModal}
    >
      {isIITLoading && <FullPageLoader />}
      <ElectronicSignature
        isModalOpen={isSignatureModalOpen}
        handleCloseModal={handleCloseSignatureModal}
        handleSignatureDiia={handleDiiaSignature}
        handleSignatureIIT={handleIITSignature}
        deepLink={deepLink}
        onIITSign={onIITSign}
        handleTimeUp={handleTimeUp}
        handlePreview={handleSignaturePreview}
        ref={timerRef}
        documents={signingDocuments}
        waitForDiiaInitialization
      />
      <ApplicationHeader {...headerProps} />

      {isUdbtfAccessDenied ? null : isApplicationLoading ? (
        <Box sx={commonStyles.centeredLoader}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={guaranteesClientPageStyles.applicationFlowStack}>
          <GuaranteesManagerApplicationSidePanelLayout
            applicationId={effectiveApplicationId}
            onError={showGuaranteesRequestError}
          >
            <InternalApprovalLetterDocumentSection
              additionalDescription={additionalDescription}
              approvalConfig={approvalConfig}
              conclusionPdfUrl={conclusionPdfUrl}
              isDocumentLoading={isDocumentLoading}
              isConclusionDocumentLoading={isConclusionDocumentLoading}
              isConclusionGenerating={isConclusionGenerating}
              isPageLoading={isApplicationLoading}
              isSignDisabled={isConclusionAttachmentRequired && !conclusionAttachmentFile}
              isSigningLoading={isIITLoading}
              isStatusUpdating={isUdbtfApprovalStatusUpdating}
              pdfUrl={pdfUrl}
              onAdditionalDescriptionChange={setAdditionalDescription}
              onAdditionalDescriptionConfirm={handleAdditionalDescriptionConfirm}
              onOpenConclusionPdf={handleOpenConclusionPdf}
              onOpenPdf={handleOpenPdf}
              onRevoke={() => setIsRevokeModalOpen(true)}
              onSignAndConfirm={() => void handleSignAndConfirm()}
            />
          </GuaranteesManagerApplicationSidePanelLayout>
        </Box>
      )}

      <RejectCommentsModal
        open={isRevokeModalOpen}
        missingCommentsCount={0}
        comment={revokeComment}
        errorText=""
        isSubmitting={isUdbtfApprovalStatusUpdating}
        onClose={() => setIsRevokeModalOpen(false)}
        onCommentChange={setRevokeComment}
        onConfirm={() => void handleRevokeConfirm()}
        title={UDBTF_APPROVAL_LETTER_REVOKE_MODAL_TEXT.title}
        primaryText={UDBTF_APPROVAL_LETTER_REVOKE_MODAL_TEXT.description}
        secondaryText=""
        commentLabel={UDBTF_APPROVAL_LETTER_REVOKE_MODAL_TEXT.label}
        cancelButtonLabel={UDBTF_APPROVAL_LETTER_REVOKE_MODAL_TEXT.cancelButton}
        confirmButtonLabel={UDBTF_APPROVAL_LETTER_REVOKE_MODAL_TEXT.confirmButton}
      />
    </ApplicationPageFrame>
  );
};

export default InternalApprovalLetterPage;
